import {
  CustomProductOptionConfigItemType,
  CustomProductOptionConfigType,
  ICustomProductOptionConfig,
  ICustomProductOptionConfigItem,
} from "@evennt-tech/api-v2-core";
import { isColorItem } from "tools/custom-product-option/common";
import { uniqBy } from "lodash";

type ColorKey = "color";
const COLOR_KEY: ColorKey = "color";
type RegularKey = `${string}_${CustomProductOptionConfigItemType}`;
type CpoKey = ColorKey | RegularKey;
export type ProductCardCustomOptions = {
  type?: CustomProductOptionConfigItemType;
  configs: ICustomProductOptionConfig[];
  cpoId: string;
};

const getGroupedConfigsByUniqueCpoItemsCount = (
  configs: ICustomProductOptionConfig[]
) => {
  // key cpoId_type(color, size_dropdown, material_image) - unique item values
  const map = new Map<CpoKey, string[]>();

  configs.forEach(({ items, type: configType }) => {
    const shouldIgnore =
      !items?.length || configType === CustomProductOptionConfigType.DEFAULT;
    if (shouldIgnore) {
      return;
    }
    const colorItems = items?.filter(isColorItem);
    if (colorItems?.length) {
      const [firstColorName, secondColorName] = getCpoColorNames({
        items: colorItems,
      } as ICustomProductOptionConfig);
      const colorValue =
        firstColorName + (secondColorName ? `_${secondColorName}` : "");
      const storedItems = map.get(COLOR_KEY) ?? [];
      if (colorValue && !storedItems.includes(colorValue)) {
        storedItems.push(colorValue);
      }
      map.set(COLOR_KEY, storedItems);
    }

    items.forEach((item) => {
      if (isColorItem(item)) {
        return;
      }
      const { cpoId, type, cpoItem } = item;
      const itemValue = cpoItem?.name ?? "";
      if (!itemValue) {
        return;
      }
      const key: RegularKey = `${cpoId}_${
        type as CustomProductOptionConfigItemType
      }`;

      const storedValues = map.get(key);
      if (storedValues === undefined) {
        map.set(key, [itemValue]);
      } else if (!storedValues.includes(itemValue)) {
        storedValues.push(itemValue);
        map.set(key, storedValues);
      }
    });
  });

  return map;
};
export const getProductCardOptions = (
  configs: ICustomProductOptionConfig[]
): ProductCardCustomOptions | null => {
  const groupedConfigCounts = getGroupedConfigsByUniqueCpoItemsCount(configs);

  const groupedConfigCountsEntries = Array.from(groupedConfigCounts.entries());
  const [firstEntry] = groupedConfigCountsEntries;
  if (!firstEntry) {
    return null;
  }

  const cpoKey: CpoKey = groupedConfigCountsEntries.reduce(
    (acc, [currKey, currValues], idx) => {
      if (idx === 0) {
        return acc;
      }
      const prevVal = groupedConfigCounts.get(acc) as string[];
      const prevLen = prevVal.length;
      if (currValues.length > prevLen) {
        return currKey;
      }
      return acc;
    },
    firstEntry[0]
  );
  const [cpoId, type] = cpoKey.split("_");

  const transformedConfigs = configs.reduce<ICustomProductOptionConfig[]>(
    (acc, curr) => {
      const filteredItems = curr.items?.filter((item) =>
        cpoKey === COLOR_KEY
          ? isColorItem(item)
          : item.type === type && item.cpoId === cpoId
      );
      if (filteredItems?.length) {
        acc.push({ ...curr, items: filteredItems });
      }

      return acc;
    },
    []
  );

  return {
    configs: transformedConfigs,
    ...(cpoKey !== COLOR_KEY && {
      type: type as CustomProductOptionConfigItemType,
    }),
    cpoId,
  };
};

export const getCpoColorHexValues = ({
  items,
}: ICustomProductOptionConfig): string[] => {
  const [firstColorItem, secondColorItem] = items ?? [];

  const res: string[] = [];
  res.push(firstColorItem?.cpoItem?.optionId ?? "");
  if (secondColorItem) {
    res.push(secondColorItem?.cpoItem?.optionId ?? "");
  }

  return res;
};
export const getCpoColorNames = <
  T extends Pick<ICustomProductOptionConfig, "items">
>({
  items,
}: T): string[] => {
  const [firstColorItem, secondColorItem] = items ?? [];

  const res: string[] = [];
  res.push(firstColorItem?.cpoItem?.name ?? "");
  if (secondColorItem) {
    res.push(secondColorItem?.cpoItem?.name ?? "");
  }

  return res;
};

export const getUniqueCpoConfigItemOptions = (
  configs: ICustomProductOptionConfig[],
  type?: CustomProductOptionConfigItemType
) => {
  return uniqBy(configs, ({ items = [] }) => getKey(items, !type));
};
const getKey = (
  items: ICustomProductOptionConfigItem[],
  isColorCriteria: boolean
) => {
  const sortedItems = items.slice(0, 2).sort((a, b) => {
    const nameA = a.cpoItem?.name ?? "";
    const nameB = b.cpoItem?.name ?? "";

    return nameA.localeCompare(nameB);
  });
  const [firstItem, secondItem] = sortedItems;

  if (isColorCriteria) {
    return `${firstItem.cpoItemId}${
      secondItem ? `_${secondItem.cpoItemId}` : ""
    }`;
  }

  return firstItem.cpoItemId;
};

export const isConfigsMatchItems = (
  configA: ICustomProductOptionConfig,
  configB: ICustomProductOptionConfig
): boolean => {
  const itemsA = configA.items;
  const itemsB = configB.items;

  return (
    !!itemsA?.length &&
    itemsA.length === itemsB?.length &&
    itemsA.every(({ cpoItemId, cpoId, type }) =>
      itemsB.some(
        (i) => i.cpoId === cpoId && i.cpoItemId === cpoItemId && type === i.type
      )
    )
  );
};
